import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { useAccount, usePublicClient, useSendTransaction } from "wagmi";
import { parseEther } from "viem";
import { CheckCircle2, FileCheck2, Coins } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { isAddress, short } from "../lib/format";
import { useApp } from "../context/AppContext";
import { PageHead } from "./Explore";
import { Panel, Loading, Empty, Unavailable } from "../components/State";
import { TokenLogo } from "../components/TokenSelect";
import { TxStatus, runTx } from "../components/TxStatus";

const STATUS_TONE = { pending_review: "text-warning bg-warning/10", approved: "text-lime bg-lime/10", rejected: "text-danger bg-danger/10", removed: "text-slate-400 bg-white/5" };

const Tiers = ({ tiers, tier, setTier, treasury }) => (
  <div className="grid md:grid-cols-2 gap-4" data-testid="listing-tiers">
    {tiers.map((t) => {
      const paid = Number(t.price_usd) > 0;
      const noPrice = paid && !t.price_eth;
      const disabled = paid && (!treasury || noPrice);
      return (
        <button key={t.id} type="button" data-testid={`listing-tier-${t.id}`} disabled={disabled} onClick={() => setTier(t.id)}
          className={`text-left glass rounded-xl p-5 transition-colors disabled:opacity-50 ${tier === t.id ? "border-lime shadow-[0_0_24px_rgba(57,255,20,0.15)]" : "hover:border-lime/30"}`}>
          <p className="font-display font-bold text-white">{t.name}</p>
          <p className="mt-1 text-2xl font-mono font-bold text-lime" data-testid={`listing-tier-price-${t.id}`}>{paid ? `$${Number(t.price_usd).toLocaleString("en-US")}` : "Free"}</p>
          {paid && <p className="text-[11px] font-mono text-slate-400" data-testid={`listing-tier-eth-${t.id}`}>{t.price_eth ? `≈ ${t.price_eth} ETH at $${Number(t.eth_usd).toLocaleString("en-US")}/ETH` : "ETH price unavailable"}</p>}
          <ul className="mt-3 space-y-1">{t.features.map((f) => <li key={f} className="flex gap-1.5 text-xs text-slate-400"><CheckCircle2 className="h-3.5 w-3.5 text-lime shrink-0" /> {f}</li>)}</ul>
          {disabled && <p className="mt-2 text-[10px] text-warning">{noPrice ? "Unavailable: live ETH price not available" : "Unavailable: treasury not configured"}</p>}
        </button>
      );
    })}
  </div>
);

const ApplyForm = ({ onCreated }) => {
  const { config, user, signIn } = useApp();
  const [f, setF] = useState({ contract_address: "", description: "", website: "", twitter: "", telegram: "", logo_url: "", contact_email: "", tier: "standard" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    if (!isAddress(f.contract_address)) return toast.error("Enter a valid 0x contract address");
    setBusy(true);
    const body = Object.fromEntries(Object.entries(f).filter(([, v]) => v !== ""));
    try { await api.post("/listings", body); toast.success("Application submitted — pending review"); onCreated(); setF({ ...f, contract_address: "", description: "" }); }
    catch (er) { toast.error(errMsg(er)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-6" data-testid="listing-apply-form">
      <Tiers tiers={config.listing_tiers} tier={f.tier} setTier={(t) => setF({ ...f, tier: t })} treasury={config.treasury_address} />
      <div className="grid md:grid-cols-2 gap-4">
        <label className="md:col-span-2 text-xs text-slate-400">Contract address (Robinhood Chain mainnet) *
          <input data-testid="listing-contract-input" value={f.contract_address} onChange={set("contract_address")} placeholder="0x…" className="field mt-1 font-mono" /></label>
        <label className="md:col-span-2 text-xs text-slate-400">Project description * (min 20 chars)
          <textarea data-testid="listing-description-input" rows={4} value={f.description} onChange={set("description")} className="field mt-1" /></label>
        {[["website", "Website URL"], ["twitter", "X / Twitter URL"], ["telegram", "Telegram URL"], ["logo_url", "Logo image URL (https)"], ["contact_email", "Contact email (private)"]].map(([k, l]) => (
          <label key={k} className="text-xs text-slate-400">{l}<input data-testid={`listing-${k}-input`} value={f[k]} onChange={set(k)} className="field mt-1" /></label>
        ))}
      </div>
      <p className="text-[11px] text-slate-500">The backend reads name, symbol, decimals and supply directly from the contract, rejects non-ERC-20 addresses and duplicates. After approval, the token owner can upload a logo from the token page.</p>
      {!config.treasury_address && <p data-testid="listing-closed-note" className="text-xs text-warning">Listing applications open once the operator sets the fee-receiving wallet.</p>}
      {user ? <button data-testid="listing-submit-button" disabled={busy || !config.treasury_address} className="btn-lime px-6 py-3 text-sm">{busy ? "Verifying on-chain…" : "Submit application"}</button>
        : <button type="button" data-testid="listing-sign-in-button" onClick={signIn} className="btn-lime px-6 py-3 text-sm">Sign in with wallet to apply</button>}
    </form>
  );
};

const PayListing = ({ l, treasury: cfgTreasury, onPaid }) => {
  const treasury = l.payment.treasury || cfgTreasury;
  const [tx, setTx] = useState(null);
  const [now, setNow] = useState(Date.now() / 1000);
  useEffect(() => { const id = setInterval(() => setNow(Date.now() / 1000), 1000); return () => clearInterval(id); }, []);
  const left = l.payment.expires_at ? Math.max(0, l.payment.expires_at - now) : null;
  const expired = left === 0;
  const requote = async () => { try { await api.post(`/listings/${l.id}/requote`); toast.success("Quote refreshed at the live ETH price"); onPaid(); } catch (e) { toast.error(errMsg(e)); } };
  const [hash, setHash] = useState("");
  const pc = usePublicClient({ chainId: 4663 });
  const { chainId } = useAccount();
  const { sendTransactionAsync } = useSendTransaction();
  const verify = async (h) => {
    try { await api.post(`/listings/${l.id}/payment`, { tx_hash: h }); toast.success("Payment verified on-chain"); onPaid(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const pay = async () => {
    if (chainId !== 4663) return toast.error("Switch your wallet to Robinhood Chain mainnet");
    if (expired) return toast.error("ETH quote expired — refresh it first");
    const rc = await runTx({ pc, setTx, label: "Listing fee", send: () => sendTransactionAsync({ chainId: 4663, to: treasury, value: parseEther(l.payment.required_eth) }) });
    if (rc) verify(rc.transactionHash);
  };
  if (!treasury) return <Unavailable title="Payments unavailable" reason="Treasury address not configured by the operator." />;
  return (
    <div className="space-y-2 mt-3">
      <p className="text-xs text-slate-400" data-testid={`listing-fee-${l.id}`}>Fee: {l.payment.required_usd && <b className="text-white">${l.payment.required_usd} = </b>}<b className="text-white">{l.payment.required_eth} ETH</b>{l.payment.eth_usd && <> at ${Number(l.payment.eth_usd).toLocaleString("en-US")}/ETH</>} to treasury <span className="font-mono">{short(treasury)}</span>. Payment buys review priority/placement — never a safety endorsement.</p>
      {left !== null && (expired
        ? <p className="text-xs text-warning flex items-center gap-2" data-testid={`listing-quote-expired-${l.id}`}>ETH quote expired. <button onClick={requote} data-testid={`listing-requote-${l.id}`} className="rounded border border-lime/40 px-2 py-0.5 text-lime">Refresh quote</button></p>
        : <p className="text-[11px] text-slate-500" data-testid={`listing-quote-timer-${l.id}`}>ETH amount locked for {Math.floor(left / 60)}:{String(Math.floor(left % 60)).padStart(2, "0")} · <button onClick={requote} className="text-lime">refresh now</button></p>)}
      <div className="flex flex-wrap gap-2">
        <button data-testid={`listing-pay-${l.id}`} onClick={pay} disabled={expired} className="btn-lime px-4 py-2 text-xs disabled:opacity-40"><Coins className="h-3.5 w-3.5" /> Pay with wallet</button>
        <input data-testid={`listing-txhash-${l.id}`} value={hash} onChange={(e) => setHash(e.target.value.trim())} placeholder="…or paste tx hash" className="field py-1.5 text-xs font-mono flex-1 min-w-[200px]" />
        <button data-testid={`listing-verify-${l.id}`} onClick={() => verify(hash)} className="rounded-lg border border-lime/30 px-3 text-xs text-lime">Verify</button>
      </div>
      <TxStatus tx={tx} />
    </div>
  );
};

const MyListings = ({ reloadKey }) => {
  const { user, config } = useApp();
  const [items, setItems] = useState(null);
  const load = useCallback(() => user && api.get("/listings/mine").then((r) => setItems(r.data)).catch(() => setItems([])), [user]);
  useEffect(() => { load(); }, [load, reloadKey]);
  if (!user) return <Empty title="Sign in to see your applications" />;
  if (items === null) return <Loading />;
  if (!items.length) return <Empty testid="my-listings-empty" title="No applications yet" />;
  return (
    <ul className="divide-y divide-white/5" data-testid="my-listings">
      {items.map((l) => (
        <li key={l.id} className="px-5 py-4">
          <div className="flex flex-wrap items-center gap-3">
            <TokenLogo t={{ symbol: l.onchain.symbol, address: l.contract_address, logo: l.metadata?.logo_url }} size={24} /><Link to={`/token/${l.contract_address}`} className="font-display font-bold text-white hover:text-lime">{l.onchain.symbol}</Link>
            <span className="text-xs font-mono text-slate-500">{short(l.contract_address, 6)}</span>
            <span data-testid={`listing-status-${l.id}`} className={`rounded px-2 py-0.5 text-[10px] font-bold uppercase ${STATUS_TONE[l.status]}`}>{l.status.replace("_", " ")}</span>
            <span className="text-[10px] font-mono text-slate-500">tier: {l.tier} · payment: {l.payment.status}</span>
          </div>
          {l.review_note && <p className="text-xs text-slate-400 mt-1">Reviewer note: {l.review_note}</p>}
          {l.payment.status === "unpaid" && <PayListing l={l} treasury={config.treasury_address} onPaid={load} />}
        </li>
      ))}
    </ul>
  );
};

export default function Listings() {
  const [k, setK] = useState(0);
  const { isConnected } = useAccount();
  return (
    <div className="mx-auto max-w-[1300px] px-4 lg:px-8 py-10 space-y-8" data-testid="listings-page">
      <PageHead kicker="Token listings" title="List your token on HIVEVORA">
        Submit a Robinhood Chain ERC-20 for review. Paid tiers buy priority review or a clearly-labelled <b>sponsored</b> placement — they are never proof of safety, legitimacy or an audit.
      </PageHead>
      <Panel title="Application" icon={FileCheck2}><div className="p-5 lg:p-8"><ApplyForm onCreated={() => setK(k + 1)} /></div></Panel>
      <Panel title="My applications">{isConnected ? <MyListings reloadKey={k} /> : <Empty title="Connect your wallet to see your applications" />}</Panel>
    </div>
  );
}
